import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import {
    CalendarIcon,
    ArrowLeftIcon,
    ExclamationTriangleIcon,
    ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { Link, useNavigate, useLocation } from 'react-router-dom';
import BookingConfirmationModal from '../../components/Modals/BookingConfirmationModal';
import SlotSkeleton from '../../components/Base/SlotSkeleton';
import Loader from 'components/Base/Loader';
import { observer } from 'mobx-react-lite';

const LIMIT = 9;

const JobCreateAthena = () => {
    const navigate = useNavigate();

    const [currentStep, setCurrentStep] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isFetching, setIsFetching] = useState(false);


    const [userStatus, setUserStatus] = useState({
        isChecking: true,
        isRegistered: false,
        isEligible: false,
        hasCompletedEligibility: false,
        hasConsentUploaded: false,
        profileData: null
    });
    const [isMessageNotify, setIsMessageNotify] = useState(false);

    const [assignedProvider, setAssignedProvider] = useState(null);
    const [reasons, setReasons] = useState([]);
    const [availableSlots, setAvailableSlots] = useState([]);


    const [selectedReason, setSelectedReason] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);

    const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(moment().add(7, 'days').format('YYYY-MM-DD'));

    const [patientData, setPatientData] = useState({
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        insurance: {
            provider: '',
            memberId: '',
            groupNumber: ''
        }
    });

    const [showConfirmation, setShowConfirmation] = useState(false);
    const [bookedAppointment, setBookedAppointment] = useState(null);

    const [isLoadingPatientData, setIsLoadingPatientData] = useState(false);
    const [isInfoConfirmed, setIsInfoConfirmed] = useState(false);
    const [isSavingChanges, setIsSavingChanges] = useState(false);
    const [changesSaved, setChangesSaved] = useState(false);

    const [departments, setDepartments] = useState([]);
    const [userDepartment, setUserDepartment] = useState(null);
    const [selectedDepartment, setSelectedDepartment] = useState(null);

    const [appointments, setAppointments] = useState([]);
    const [cancelReason, setCancelReason] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [modalAppointment, setModalAppointment] = useState(null);
    const [completed, setCompleted] = useState(false);
    const [showSlotsModal, setShowSlotsModal] = useState(false);
    const [rescheduleReason, setRescheduleReason] = useState('');
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [currentRescheduleStep, setCurrentRescheduleStep] = useState('slots');



    const checkUserStatus = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const [profileRes, athenaProfileRes] = await Promise.all([
                axios.get('/profile/general'),
                axios.get('/athena/profile'),
            ]);

            const profileData = profileRes.data;
            const athena = athenaProfileRes.data || {};

            const isRegistered = !!athena.isRegistered;
            const hasCompletedEligibility = !!(
                athena.eligibility?.primaryInsurance?.isEligibilityChecked ||
                athena.eligibility?.secondaryInsurance?.isEligibilityChecked
            );
            const isEligible = !!(
                athena.eligibility?.primaryInsurance?.isEligible ||
                athena.eligibility?.secondaryInsurance?.isEligible
            );
            const hasConsentUploaded = !!athena.consent?.isConsentGiven;

            setIsMessageNotify(profileData.messageNotify);
            setUserStatus({
                isChecking: false,
                isRegistered,
                isEligible,
                hasCompletedEligibility,
                hasConsentUploaded,
                profileData,
            });

            await fetchInitialData();
        } catch (error) {
            console.error('Error checking user status:', error);
            setError('Failed to verify your eligibility status. Please try again.');
            setUserStatus(prev => ({ ...prev, isChecking: false }));
            setIsLoading(false);
        }
    };

    const fetchAppointments = useCallback(() => {
        axios
            .get('/athena/appointments?upcoming=true')
            .then(({ data }) => {
                setAppointments(data.appointments || []);
            })
            .catch((e) => {
                console.log(e);
            })
    }, []);

    const is24HoursMore = (appointmentDate, appointmentTime) => {
        if (!appointmentDate) return false;
        let appointmentDateTime = moment(appointmentDate, 'YYYY-MM-DD');
        if (appointmentTime) {
            appointmentDateTime = moment(`${appointmentDate} ${appointmentTime}`, 'YYYY-MM-DD HH:mm');
        }

        const now = moment();
        const hoursUntilAppointment = appointmentDateTime.diff(now, 'hours', true);

        return hoursUntilAppointment > 0 && hoursUntilAppointment >= 24;
    };

    const handleCancelClick = (appointment) => {
        if (is24HoursMore(appointment.date, appointment.startTime)) {
            setModalAppointment(appointment);
            setShowModal(true);
        } else {
            setModalAppointment(appointment);
            setShowModal(true);
        }
    };

    const handleRescheduleClick = (appointment) => {
        setModalAppointment(appointment);
        setShowSlotsModal(true);
        setCurrentRescheduleStep('slots');
        fetchRescheduleSlots(appointment);
    };

    const fetchRescheduleSlots = useCallback(async (appointment) => {
        setLoadingSlots(true);
        try {
            const appointmentDetailsResponse = await axios.get(`/athena/appointment/${appointment.athenaAppointmentId || appointment.jobId}/details`);
            const appointmentDetails = appointmentDetailsResponse.data[0] || appointmentDetailsResponse.data;

            if (!appointmentDetails || !appointmentDetails.departmentid) {
                throw new Error('Could not get department information from appointment');
            }

            const response = await axios.post('/athena/appointment/open', {
                startDate: moment().format('YYYY-MM-DD'),
                endDate: moment().add(7, 'days').format('YYYY-MM-DD'),
                departmentId: appointmentDetails.departmentid,
                reasonId: appointment.reasonId
            }, { timeout: 120000 });
            setAvailableSlots(response.data);
        } catch (error) {
            console.error("Error fetching available slots:", error);
            setAvailableSlots([]);
        } finally {
            setLoadingSlots(false);
        }
    }, []);

    const handleRescheduleSlotSelection = useCallback((slot) => {
        setSelectedSlot(slot);
    }, []);

    const handleRescheduleConfirmSlot = useCallback(() => {
        setCurrentRescheduleStep('reason');
    }, []);

    const handleRescheduleBackToSlots = useCallback(() => {
        setCurrentRescheduleStep('slots');
        setRescheduleReason('');
    }, []);

    const handleConfirmReschedule = useCallback(async () => {
        if (!rescheduleReason.trim()) return;

        setIsFetching(true);
        try {
            await axios.post(
                `/athena/appointment/${modalAppointment.jobId}/reschedule`,
                {
                    appointmentType: selectedSlot.appointmentType,
                    reason: rescheduleReason.trim(),
                    newappointmentid: selectedSlot.appointmentid,
                    date: selectedSlot.date,
                    startTime: selectedSlot.starttime,
                },
                { timeout: 120000 }
            );

            setCompleted(true);
            setShowSlotsModal(false);
            setCurrentRescheduleStep('slots');
            setSelectedSlot(null);
            setRescheduleReason('');
            setAvailableSlots([]);
            fetchAppointments();
        } catch (err) {
            console.error('Error rescheduling appointment:', err);
            setError('Failed to reschedule appointment. Time slot may no longer be available. Please try again.');
        } finally {
            setIsFetching(false);
        }
    }, [modalAppointment, selectedSlot, rescheduleReason, fetchAppointments]);

    const sendCancelAppointment = useCallback(() => {
        if (!cancelReason.trim()) {
            return;
        }

        setIsFetching(true);

        axios.post(`/athena/appointment/${modalAppointment.jobId}/cancel`, {
            reason: cancelReason.trim()
        }, { timeout: 120000 })
            .then(() => {
                setCompleted(true);
                setShowModal(false);
                setCancelReason('');
                fetchAppointments();
            })
            .catch((error) => {
                setError('Failed to cancel appointment. Please try again or contact support if the issue persists.');
            })
            .finally(() => {
                setIsFetching(false);
            });
    }, [fetchAppointments, modalAppointment, cancelReason]);

    useEffect(() => {
        fetchAppointments();
        checkUserStatus();
    }, []);


    const fetchInitialData = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const profileRes = await axios.get('/athena/profile');

            if (profileRes.data?.departmentId) {
                setUserDepartment(profileRes.data.departmentId);
                setSelectedDepartment(profileRes.data.departmentId);
            }

            const departmentsRes = await axios.get('/athena/departments');
            setDepartments(departmentsRes.data);

            if (profileRes.data.assignedProviderId) {
                const providersInfo = await axios.get(`/athena/providers/${profileRes.data.assignedProviderId}`);
                setAssignedProvider(providersInfo.data);
                fetchReasons(profileRes.data.assignedProviderId, profileRes.data.departmentId);
            }
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching initial data:', error);
            setError('Failed to load initial data. Please try again.');
            setIsLoading(false);
        }
    };

    const fetchReasons = async (assignedProviderId, userDepartment) => {
        try {
            // if (!providerId || !departmentId) {
            //     throw new Error('Provider ID and Department ID are required');
            // }
            setIsLoading(true);
            setError(null);
            const reasonsRes = await axios.get(`/athena/appointment/reason/${assignedProviderId}/${userDepartment}`);
            setReasons(reasonsRes.data.patientappointmentreasons);
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching reasons:', error);
            setError('Failed to load appointment reasons. Please try again.');
            setIsLoading(false);
        }
    };

    const handleReasonSelection = async (reason) => {
        setSelectedReason(reason);
        setCurrentStep(2);
        await fetchAvailableSlots(reason.reasonid, assignedProvider.id);
    };

    const fetchAvailableSlots = async (reasonId, providerId, start = startDate, end = endDate) => {
        try {
            setIsLoading(true);
            const response = await axios.post('/athena/appointment/open',
                {
                    providerId,
                    reasonId,
                    startDate: start,
                    endDate: end,
                    departmentId: selectedDepartment
                }
            );

            setAvailableSlots(response.data);
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching slots:', error);
            setError('Failed to load available time slots.');
            setIsLoading(false);
        }
    };

    const changeWeek = (direction) => {

        const currentStart = moment(startDate);
        const newStart = currentStart.clone().add(direction, 'weeks');
        const newEnd = newStart.clone().add(6, 'days');

        const sDateFormatted = newStart.format('YYYY-MM-DD');
        const eDateFormatted = newEnd.format('YYYY-MM-DD');

        setStartDate(sDateFormatted);
        setEndDate(eDateFormatted);

        if (selectedReason && assignedProvider) {
            fetchAvailableSlots(selectedReason.reasonid, assignedProvider.id, sDateFormatted, eDateFormatted);
        }
    };

    const handleSlotSelection = async (slot) => {
        setSelectedSlot(slot);
        setCurrentStep(3);
        await fetchAthenaPatientData();
    };

    const fetchAthenaPatientData = async () => {
        try {
            setIsLoadingPatientData(true);
            setError(null);

            const response = await axios.get('/athena/patient/info');

            setPatientData({
                firstName: response.data[0].firstname || '',
                lastName: response.data[0].lastname || '',
                dateOfBirth: response.data[0].dob ? moment(response.data[0].dob, 'MM/DD/YYYY').format('YYYY-MM-DD') : '',
                email: response.data[0].email || '',
                phone: '',
                address: '',
                city: response.data[0].city || '',
                state: response.data[0].state || '',
                zipCode: response.data[0].zip || '',
            });

            setIsLoadingPatientData(false);
        } catch (error) {
            console.error('Error fetching patient data:', error);
            setError('Failed to load your patient information. Please try again.');
            setIsLoadingPatientData(false);
        }
    };

    const handlePatientDataChange = (field, value) => {
        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            setPatientData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setPatientData(prev => ({
                ...prev,
                [field]: value
            }));
        }
        setChangesSaved(false);
    };

    const saveChanges = async () => {
        try {
            setIsSavingChanges(true);
            setError(null);

            const updateData = {
                firstname: patientData.firstName,
                lastname: patientData.lastName,
                email: patientData.email,
                city: patientData.city,
                state: patientData.state,
                zip: patientData.zipCode
            };

            await axios.put('/athena/customer/update', updateData);

            setChangesSaved(true);
            setTimeout(() => setChangesSaved(false), 3000);

        } catch (error) {
            console.error('Error saving changes:', error);
            setError('Failed to save changes. Please try again.');
        } finally {
            setIsSavingChanges(false);
        }
    };

    const handleBooking = async () => {
        try {
            setIsLoading(true);

            const appointmentData = {
                appointmentTypeId: selectedSlot.appointmenttypeid,
                reasonid: selectedReason.reasonid,
                date: selectedSlot.date,
                appointmentId: selectedSlot.appointmentid,
                providerName: assignedProvider.name,
                departmentId: selectedDepartment,
                providerId: assignedProvider.id,
            };

            const bookingResponse = await axios.post('/athena/appointment/create', appointmentData, {
                timeout: 600000
            });

            const responseData = bookingResponse.data;

            const confirmationData = {
                ...responseData,
                providerName: assignedProvider.name,
                location: 'Virtual Appointment',
            };

            if (!responseData.createdCount && responseData.jobId) {
                confirmationData.appointment = {
                    startTime: selectedSlot.starttime || selectedSlot.time,
                    duration: selectedSlot.duration,
                    date: selectedSlot.date
                };
            }

            setBookedAppointment(confirmationData);
            setShowConfirmation(true);
        } catch (error) {
            console.error('Error booking appointment:', error);
            setError('Failed to book appointment. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const resetForm = () => {
        setCurrentStep(0);
        setSelectedReason(null);
        setSelectedSlot(null);
        setPatientData({
            firstName: '',
            lastName: '',
            dateOfBirth: '',
            email: '',
            phone: '',
            address: '',
            city: '',
            state: '',
            zipCode: '',
            insurance: {
                provider: '',
                memberId: '',
                groupNumber: ''
            }
        });
        setIsInfoConfirmed(false);
        setIsSavingChanges(false);
        setChangesSaved(false);
        setError(null);
    };

    const handleConfirmationClose = () => {
        setShowConfirmation(false);
        resetForm();
    };

    const handleViewAppointments = () => {
        setShowConfirmation(false);
        navigate('/jobs/athena')
    };

    const goBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const renderStepIndicator = () => (
        <div className="mb-8">
            <div className="flex items-center justify-center space-x-4">
                {[1, 2, 3].map((step) => (
                    <div key={step} className="flex items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step <= currentStep
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-600'
                            }`}>
                            {step}
                        </div>
                        {step < 3 && (
                            <div className={`w-12 h-0.5 mx-2 ${step < currentStep ? 'bg-blue-600' : 'bg-gray-200'
                                }`} />
                        )}
                    </div>
                ))}
            </div>
            <div className="flex justify-center mt-2 text-sm text-gray-600">
                {currentStep === 1 && 'Select Appointment Reason'}
                {currentStep === 2 && 'Choose Time Slot'}
                {currentStep === 3 && 'Confirm Information'}
            </div>
        </div>
    );

    const renderStep1 = () => (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Select Appointment Reason</h2>
                <p className="text-gray-600 mb-6">
                    Why are you scheduling this appointment?
                </p>
                {assignedProvider && (
                    <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                        <div className="text-sm text-blue-800">
                            <strong>Selected Provider:</strong> {assignedProvider.name}
                        </div>
                    </div>
                )}
            </div>

            {isLoading ? (
                <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="mt-4 text-sm text-gray-500">Loading appointment reasons...</p>
                </div>
            ) : reasons.length === 0 ? (
                <div className="text-center py-12">
                    <div className="text-gray-500 mb-4">No appointment reasons available for this provider.</div>
                    <button
                        onClick={() => setCurrentStep(1)}
                        className="text-blue-600 hover:text-blue-800"
                    >
                        Choose a different provider
                    </button>
                </div>
            ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {reasons.map((reason, index) => (
                        <button
                            key={index}
                            onClick={() => handleReasonSelection(reason)}
                            className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all text-left"
                        >
                            <div className="font-medium text-gray-900">{reason.name}</div>
                            {reason.description && (
                                <div className="text-sm text-gray-500 mt-1">{reason.description}</div>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );

    const renderStep2 = () => (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Choose Available Time Slot</h2>
                <p className="text-gray-600 mb-6">
                    Select a convenient time for your appointment
                </p>
                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                    <div className="text-sm text-blue-800">
                        <strong> Reason:</strong> {selectedReason?.reason}
                    </div>
                </div>
            </div>

            {/* Date Range Picker */}
            <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Select Week</h3>

                <div className="flex items-center justify-between bg-white p-2 border border-gray-300 rounded-lg">
                    <button
                        onClick={() => changeWeek(-1)}
                        disabled={moment(startDate).add(-1, 'weeks').endOf('week').isBefore(moment())}
                        className="p-2 hover:bg-gray-100 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    <div className="text-center">
                        <span className="text-sm font-medium text-gray-900 block">
                            {moment(startDate).format('MMM D')} - {moment(endDate).format('MMM D, YYYY')}
                        </span>
                    </div>

                    <button
                        onClick={() => changeWeek(1)}
                        disabled={moment(endDate).add(1, 'weeks').isAfter(moment().add(30, 'days'))}
                        className="p-2 hover:bg-gray-100 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>
            </div>
            <div className="mt-3 px-3">
                <p className="text-sm text-gray-600">
                    Need to adjust your care plan or request a different therapist?{' '}
                    <a href="tel:+16172370589" className="text-blue-600 hover:text-blue-800">
                        Call us directly at +1 (617) 237-0589
                    </a>{' '}
                    and our care coordinators will gladly help.
                </p>
            </div>

            {isLoading ? (
                <SlotSkeleton count={6} />
            ) : availableSlots.length === 0 ? (
                <div className="text-center py-12">
                    <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No available slots found for the selected criteria.</p>
                    <button
                        onClick={() => setCurrentStep(1)}
                        className="mt-4 text-blue-600 hover:text-blue-800"
                    >
                        Try different provider or reason
                    </button>
                </div>
            ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {availableSlots.map((slot, index) => (
                        <button
                            key={index}
                            onClick={() => handleSlotSelection(slot)}
                            className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all text-left"
                        >
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                    <CalendarIcon className="h-5 w-5 text-green-600" />
                                </div>
                                <div>
                                    <div className="font-medium text-gray-900">
                                        {moment(slot.date).format('MMM D, YYYY')}
                                    </div>
                                    <div className="text-sm text-gray-500">{slot.starttime}</div>
                                    {slot.duration && (
                                        <div className="text-xs text-gray-400">{slot.duration} min</div>
                                    )}
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );

    const renderStep3 = () => (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Confirm Your Information</h2>
                <p className="text-gray-600 mb-6">
                    Please review and update your information before booking
                </p>
            </div>

            {/* Appointment Summary */}
            <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <h3 className="text-lg font-medium text-blue-900 mb-3">Appointment Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                        <span className="font-medium text-blue-800">Provider:</span>
                        <span className="ml-2 text-blue-700">{assignedProvider?.name}</span>
                    </div>
                    <div>
                        <span className="font-medium text-blue-800">Reason:</span>
                        <span className="ml-2 text-blue-700">{selectedReason?.reason}</span>
                    </div>
                    <div>
                        <span className="font-medium text-blue-800">Date:</span>
                        <span className="ml-2 text-blue-700">{moment(selectedSlot?.date).format('MMM D, YYYY')}</span>
                    </div>
                    <div>
                        <span className="font-medium text-blue-800">Time:</span>
                        <span className="ml-2 text-blue-700">{selectedSlot?.starttime}</span>
                    </div>
                    <div>
                        <span className="font-medium text-blue-800">Duration:</span>
                        <span className="ml-2 text-blue-700">{selectedSlot?.duration} minutes</span>
                    </div>
                </div>
            </div>


            {isLoadingPatientData ? (
                <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="mt-4 text-sm text-gray-500">Loading your information...</p>
                </div>
            ) : (
                <>
                    {/* Patient Information Form */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Your Information</h3>

                        {/* Success Message */}
                        {changesSaved && (
                            <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm text-green-800">Your information has been saved successfully!</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="grid gap-6 sm:grid-cols-2">
                            {/* Personal Information */}
                            <div className="space-y-4">
                                <h4 className="text-md font-medium text-gray-700">Contact Information</h4>


                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                                    <input
                                        type="email"
                                        value={patientData.email}
                                        onChange={(e) => handlePatientDataChange('email', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                                    <input
                                        type="tel"
                                        value={patientData.phone}
                                        onChange={(e) => handlePatientDataChange('phone', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Address and Insurance */}
                            <div className="space-y-4">
                                <h4 className="text-md font-medium text-gray-700">Address</h4>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                                    <input
                                        type="text"
                                        value={patientData.address}
                                        onChange={(e) => handlePatientDataChange('address', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                                        <input
                                            type="text"
                                            value={patientData.city}
                                            onChange={(e) => handlePatientDataChange('city', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                                        <input
                                            type="text"
                                            value={patientData.state}
                                            onChange={(e) => handlePatientDataChange('state', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                                    <input
                                        type="text"
                                        value={patientData.zipCode}
                                        onChange={(e) => handlePatientDataChange('zipCode', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>

                            </div>
                        </div>

                        {/* Save Changes Button */}
                        <div className="mt-6 pt-4 border-t border-gray-200">
                            <button
                                onClick={saveChanges}
                                disabled={isSavingChanges}
                                className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            >
                                {isSavingChanges ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Save Changes
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Confirmation Checkbox */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex items-start space-x-3">
                            <input
                                type="checkbox"
                                id="confirmInfo"
                                checked={isInfoConfirmed}
                                onChange={(e) => setIsInfoConfirmed(e.target.checked)}
                                className="mt-0.5 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label htmlFor="confirmInfo" className="text-sm text-gray-700">
                                I confirm that all the information above is correct and I authorize this appointment booking.
                            </label>
                        </div>
                    </div>

                    {/* Booking Time Notice */}
                    <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-amber-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-amber-800">Important Notice</h3>
                                <div className="mt-1 text-sm text-amber-700">
                                    <p>Booking your appointment may take up to <strong>10 minutes</strong> to complete. Please do not close this page or refresh while the booking is in progress.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-4 pt-6">
                        <button
                            onClick={goBack}
                            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                        >
                            Back
                        </button>
                        <button
                            onClick={handleBooking}
                            disabled={isLoading || !isInfoConfirmed}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? (
                                <div className="flex items-center">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Booking...
                                </div>
                            ) : 'Book Appointment'}
                        </button>
                    </div>
                </>
            )}
        </div>
    );

    if (!userStatus.isChecking && !userStatus.isEligible && userStatus.hasCompletedEligibility) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-sky-50 flex items-center justify-center">
                <div className="max-w-md mx-auto text-center">
                    <div className="bg-white rounded-2xl shadow-lg shadow-sky-900/5 ring-1 ring-slate-100 p-8">
                        <div className="mx-auto w-16 h-16 mb-6 rounded-full bg-red-50 flex items-center justify-center">
                            <ExclamationTriangleIcon className="w-8 h-8 text-red-600" />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-4">
                            Not Eligible for Medicare Services
                        </h1>
                        <p className="text-gray-600 mb-6">
                            Based on your eligibility check, you are not eligible for Medicare-covered services at this time.
                        </p>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                            <p className="text-sm text-red-800">
                                Please contact our office for alternative payment options or to discuss other available services.
                            </p>
                        </div>
                        <div className="space-y-3">
                            <Link
                                to="/eligibility"
                                className="block w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Update Eligibility Information
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (userStatus.isChecking || (isLoading && currentStep === 1)) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-sky-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="mt-4 text-sm text-gray-500">
                        {userStatus.isChecking ? 'Checking your eligibility status...' : 'Loading appointment data...'}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-sky-50">
            <div className="mx-auto pt-8 pb-8" style={{ maxWidth: '1100px' }}>
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-blue-500 via-sky-500 to-sky-700 bg-clip-text text-transparent">
                        Schedule Appointment
                    </h1>
                    <p className="mt-2 text-sm text-gray-600">
                        Book your appointment in just a few simple steps
                    </p>
                </div>

                {/* Eligibility Success Message */}
                {!userStatus.isChecking && userStatus.isEligible && userStatus.hasConsentUploaded && (
                    <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <ShieldCheckIcon className="h-5 w-5 text-green-400" />
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-green-800">
                                    <strong>All Requirements Met:</strong> Your eligibility has been verified and consent form uploaded. You can now proceed with scheduling your appointment.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* SMS Notifications Reminder */}
                {!isMessageNotify && (
                    <div className="mb-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <svg className="h-6 w-6 text-green-500 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <div className="ml-3 flex-1">
                                <h3 className="text-sm font-medium text-green-900">Stay Updated with SMS Notifications</h3>
                                <p className="mt-2 text-sm text-green-700">
                                    Don't miss important updates about your appointment confirmations, reminders, and service notifications.
                                    Enable SMS notifications to stay informed about your care.
                                </p>
                                <div className="mt-3">
                                    <button
                                        onClick={() => navigate('/profile?tab=General Information')}
                                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
                                    >
                                        <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        Manage Notifications
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Upcoming Appointment */}
                {currentStep === 0 && (
                    <div className="mb-6 bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="text-lg font-semibold text-slate-800">Upcoming Appointment</h2>
                        </div>

                        {appointments.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-6 text-center bg-slate-50 rounded-lg border border-dashed border-slate-300">

                                {!assignedProvider ? (
                                    <div className="max-w-lg">
                                        <h3 className="text-xl font-bold text-green-600 mb-2">Your request is in!</h3>
                                        <p className="text-sm text-slate-600 leading-relaxed">
                                            We are securely verifying your Medicare benefits to ensure you have no surprise bills.
                                            We will call or email you within 48 hours to confirm your visit
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <p className="text-slate-600 mb-4">You have no upcoming appointments at this time.</p>
                                        <button
                                            onClick={() => setCurrentStep(1)}
                                            className="w-full sm:w-auto px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg shadow-md transition-all active:scale-95"
                                        >
                                            Book Next Visit with {assignedProvider.name}
                                        </button>
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                                <div className="flex justify-between items-start gap-2">
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-slate-700">
                                            {appointments[0].description || 'No description available'}
                                        </p>
                                        <p className="text-xs text-slate-500">
                                            {appointments[0].title || appointments[0].appointmentType || 'Appointment'}
                                        </p>
                                    </div>
                                    <span className="text-xs text-slate-500 whitespace-nowrap">
                                        {appointments[0].status ? appointments[0].status.toUpperCase() : 'N/A'}
                                    </span>
                                </div>

                                <div className="mt-2 text-sm text-slate-600 mb-3">
                                    {appointments[0].date ? moment(appointments[0].date).format('MMM DD, YYYY') : 'Date not set'}
                                    {appointments[0].startTime && (
                                        <span> • {moment(appointments[0].startTime, 'HH:mm').format('hh:mm A')}</span>
                                    )}
                                    {appointments[0].duration && (
                                        <span> • {appointments[0].duration} min</span>
                                    )}
                                </div>

                                {is24HoursMore(appointments[0].date, appointments[0].startTime) ? (
                                    <div className="flex gap-2 justify-end">
                                        <button
                                            onClick={() => handleRescheduleClick(appointments[0])}
                                            className="text-xs px-3 py-1.5 border border-blue-200 text-blue-600 rounded-md hover:bg-blue-50 transition-colors"
                                            title="Reschedule this appointment"
                                        >
                                            Reschedule
                                        </button>
                                        <button
                                            onClick={() => handleCancelClick(appointments[0])}
                                            className="text-xs px-3 py-1.5 border border-red-200 text-red-600 rounded-md hover:bg-red-50 transition-colors"
                                            title="Cancel this appointment"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                ) : (
                                    <div className="text-xs text-amber-600 mt-2 bg-amber-50 p-2 rounded border border-amber-100">
                                        To cancel or reschedule an appointment within 24 hours of your visit, please call us directly at +1 (617) 237-0589 so we can notify your therapist.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
                {/* Error Message */}
                {error && (
                    <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-800">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Step Indicator */}
                {currentStep > 0 && renderStepIndicator()}

                {/* Main Content */}
                <div className="bg-white rounded-2xl shadow-lg shadow-sky-900/5 ring-1 ring-slate-100 p-6">
                    {/* Back Button */}
                    {currentStep > 1 && (
                        <button
                            onClick={goBack}
                            className="mb-6 flex items-center text-gray-600 hover:text-gray-800"
                        >
                            <ArrowLeftIcon className="h-4 w-4 mr-2" />
                            Back
                        </button>
                    )}

                    {/* Step Content */}
                    {currentStep === 1 && renderStep1()}
                    {currentStep === 2 && renderStep2()}
                    {currentStep === 3 && renderStep3()}
                </div>
            </div>

            {/* Confirmation Modal */}
            {showConfirmation && (
                <BookingConfirmationModal
                    isOpen={showConfirmation}
                    onClose={handleConfirmationClose}
                    appointmentData={bookedAppointment}
                    onViewAppointments={handleViewAppointments}
                />
            )}

            {/* 24-Hour Restriction Modal */}
            {/* {show24HourModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
                        <div className="flex items-start mb-4">
                            <div className="flex-shrink-0">
                                <PhoneIcon className="h-6 w-6 text-amber-600" />
                            </div>
                            <div className="ml-3">
                                <h3 className="text-lg font-medium text-gray-900">
                                    {appointment24HourMessage.type === 'cancel' ? 'Cannot Cancel' : 'Cannot Reschedule'}
                                </h3>
                            </div>
                        </div>
                        <div className="text-sm text-gray-600 mb-6">
                            <p className="mb-3">
                                To cancel or reschedule an appointment within 24 hours of your visit, please call us directly at:
                            </p>
                            <p className="font-semibold text-lg text-blue-600 mb-3">
                                (617) 934-8612
                            </p>
                            <p className="text-xs text-gray-500">
                                This allows us to notify your therapist in time.
                            </p>
                        </div>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setShow24HourModal(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                                Dismiss
                            </button>
                        </div>
                    </div>
                </div>
            )} */}

            {/* Cancel Appointment Modal */}
            {showModal && modalAppointment && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
                        <div className="flex items-start mb-4">
                            <div className="flex-shrink-0">
                                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                            </div>
                            <div className="ml-3">
                                <h3 className="text-lg font-medium text-gray-900">Cancel Appointment</h3>
                                <p className="text-sm text-gray-600 mt-1">
                                    Are you sure you want to cancel this appointment?
                                </p>
                            </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 mb-4">
                            <div className="text-sm text-gray-700">
                                <p className="font-medium">{modalAppointment.description || 'Appointment'}</p>
                                <p className="text-gray-500">
                                    {modalAppointment.date ? moment(modalAppointment.date).format('MMM DD, YYYY') : 'Date not set'}
                                    {modalAppointment.startTime && (
                                        <span> • {moment(modalAppointment.startTime, 'HH:mm').format('hh:mm A')}</span>
                                    )}
                                </p>
                            </div>
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Reason for cancellation
                            </label>
                            <textarea
                                value={cancelReason}
                                onChange={(e) => setCancelReason(e.target.value)}
                                placeholder="Please provide a reason..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                rows={3}
                            />
                        </div>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => {
                                    setShowModal(false);
                                    setCancelReason('');
                                    setError('');
                                }}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                                Keep Appointment
                            </button>
                            <button
                                onClick={sendCancelAppointment}
                                disabled={!cancelReason.trim()}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                            >
                                Cancel Appointment
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reschedule Appointment Modal */}
            {showSlotsModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
                        {currentRescheduleStep === 'slots' ? (
                            <>
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-start">
                                        <div className="ml-3">
                                            <h3 className="text-lg font-medium text-gray-900">Reschedule Appointment</h3>
                                            <p className="text-sm text-gray-600 mt-1">
                                                Select a new date and time for your appointment
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setShowSlotsModal(false);
                                            setSelectedSlot(null);
                                            setRescheduleReason('');
                                            setCurrentRescheduleStep('slots');
                                            setAvailableSlots([]);
                                            setError('');
                                        }}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        <span className="sr-only">Close</span>
                                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                {loadingSlots ? (
                                    <div className="flex justify-center items-center min-h-[300px]">
                                        <div className="text-center">
                                            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                                            <p className="mt-4 text-gray-600">Loading available slots...</p>
                                        </div>
                                    </div>
                                ) : availableSlots.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center min-h-[300px] text-gray-500">
                                        <svg className="w-16 h-16 mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <p className="text-lg font-medium">No Available Slots</p>
                                        <p className="mt-2 text-sm text-gray-400 text-center px-4">There are no appointment slots available for the selected date range.</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="space-y-4 max-h-[500px] overflow-y-auto mb-6">
                                            {Object.entries(
                                                availableSlots.reduce((acc, slot) => {
                                                    const date = moment(slot.date, 'MM/DD/YYYY').format('YYYY-MM-DD');
                                                    if (!acc[date]) acc[date] = [];
                                                    acc[date].push(slot);
                                                    return acc;
                                                }, {})
                                            ).map(([date, slots]) => (
                                                <div key={date} className="bg-gray-50 rounded-lg p-4">
                                                    <h3 className="text-lg font-semibold text-gray-800 mb-3">
                                                        {moment(date).format('ddd, MMM D, YYYY')}
                                                    </h3>
                                                    <div className="grid grid-cols-2 gap-3">
                                                        {slots.map((slot, index) => (
                                                            <button
                                                                key={index}
                                                                onClick={() => handleRescheduleSlotSelection(slot)}
                                                                className={`flex flex-col p-3 rounded-lg transition-all duration-200 border-2 ${selectedSlot === slot
                                                                    ? 'border-blue-500 bg-blue-50 shadow-sm'
                                                                    : 'border-transparent bg-white hover:border-gray-200 hover:shadow-sm'
                                                                    }`}
                                                            >
                                                                <div className="flex items-center gap-1.5 mb-1.5">
                                                                    <svg className="w-3.5 h-3.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                    </svg>
                                                                    <span className="font-medium text-gray-900 text-sm">
                                                                        {slot.starttime}
                                                                    </span>
                                                                    <span className="text-xs text-gray-500">
                                                                        ({slot.duration}m)
                                                                    </span>
                                                                </div>
                                                                <div className="text-xs font-medium text-gray-700 truncate">
                                                                    {slot.patientappointmenttypename || 'Standard Appointment'}
                                                                </div>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {selectedSlot && (
                                            <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                                                <button
                                                    onClick={() => setSelectedSlot(null)}
                                                    className="text-sm text-gray-600 hover:text-gray-800"
                                                >
                                                    ← Choose different slot
                                                </button>
                                                <button
                                                    onClick={handleRescheduleConfirmSlot}
                                                    className="px-6 py-2.5 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700 transition-colors"
                                                >
                                                    Continue
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                <div className="flex items-center gap-2 mb-6">
                                    <button
                                        onClick={handleRescheduleBackToSlots}
                                        className="text-gray-600 hover:text-gray-800"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                        </svg>
                                    </button>
                                    <h2 className="text-lg font-bold text-gray-800">Reschedule Reason</h2>
                                </div>

                                <div className="mb-4">
                                    <div className="bg-gray-50 rounded-lg p-3 mb-6">
                                        <p className="text-sm text-gray-600 mb-2">Selected Time:</p>
                                        <div className="font-medium text-gray-900">
                                            {moment(selectedSlot?.date, 'MM/DD/YYYY').format('dddd, MMMM D, YYYY')}
                                        </div>
                                        <div className="text-gray-600">
                                            {selectedSlot?.starttime} ({selectedSlot?.duration} minutes)
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-6">
                                    <label htmlFor="rescheduleReason" className="block text-sm font-medium text-gray-700 mb-2">
                                        Reason for Rescheduling
                                    </label>
                                    <textarea
                                        id="rescheduleReason"
                                        value={rescheduleReason}
                                        onChange={(e) => setRescheduleReason(e.target.value)}
                                        placeholder="Enter your reason here..."
                                        className="w-full min-h-[100px] p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                    />
                                    {rescheduleReason.trim() === '' && (
                                        <p className="mt-2 text-xs text-red-600">Please provide a reason for rescheduling.</p>
                                    )}
                                    {error && (
                                        <p className="mt-2 text-xs text-red-600">{error}</p>
                                    )}
                                </div>

                                <div className="flex justify-end">
                                    <button
                                        onClick={handleConfirmReschedule}
                                        disabled={!rescheduleReason.trim()}
                                        className={`px-6 py-2.5 text-sm font-medium rounded-lg transition-colors ${rescheduleReason.trim()
                                            ? "bg-blue-600 text-white hover:bg-blue-700"
                                            : "bg-blue-300 text-white cursor-not-allowed"
                                            }`}
                                    >
                                        Confirm Reschedule
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
            <Loader show={isFetching} />
        </div>
    );
};

export default observer(JobCreateAthena);

